<script lang="ts" setup>
import type { CalendarCellTriggerProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { CalendarCellTrigger, useForwardProps } from 'reka-ui'
import { cn } from '@/lib/utils'

const props = defineProps<
  CalendarCellTriggerProps & { class?: HTMLAttributes['class']; disableLink?: boolean }
>()

const delegatedProps = reactiveOmit(props, 'class')

const forwardedProps = useForwardProps(delegatedProps)
const { tm, t } = useI18n()

// Format date for aria-label
const formatAriaLabel = (date: { year: number; month: number; day: number }): string => {
  const dateObj = new Date(date.year, date.month - 1, date.day)
  const weekdaysRaw = tm('ui.calendar.weekdays')
  const monthsRaw = tm('ui.calendar.months_genitive')

  // Convert to plain arrays
  const weekdays: string[] = Object.values(weekdaysRaw as Record<string, string>)
  const months: string[] = Object.values(monthsRaw as Record<string, string>)

  const weekday = String(weekdays[dateObj.getDay()] || '')
  const day = date.day
  const month = String(months[date.month - 1] || '')
  const year = date.year

  return `${weekday}, ${day} ${month} ${year} ${t('ui.calendar.year_abbreviation')}`
}

// Create news URL
const createNewsUrl = (date: { year: number; month: number; day: number }): string => {
  const month = date.month.toString().padStart(2, '0')
  const day = date.day.toString().padStart(2, '0')
  return `/news/${date.year}/${month}/${day}`
}

const ariaLabel = computed(() => formatAriaLabel(props.day))
const newsUrl = computed(() => createNewsUrl(props.day))

const cellClasses = computed(() =>
  cn(
    'mx-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg p-0 text-sm font-medium transition-all duration-200',
    'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1',
    // Normal dates
    'text-gray-700 hover:bg-blue-50 hover:text-blue-700',
    // Today
    '[&[data-today]:not([data-selected])]:bg-blue-100 [&[data-today]:not([data-selected])]:font-semibold [&[data-today]:not([data-selected])]:text-blue-700',
    // Selected
    'data-[selected]:bg-blue-600 data-[selected]:font-semibold data-[selected]:text-white data-[selected]:hover:bg-blue-700',
    // Unavailable
    'data-[unavailable]:cursor-not-allowed data-[unavailable]:text-gray-300 data-[unavailable]:hover:bg-transparent data-[unavailable]:hover:text-gray-300',
    'data-[disabled]:cursor-not-allowed data-[disabled]:text-gray-300 data-[disabled]:hover:bg-transparent data-[disabled]:hover:text-gray-300 data-[disabled]:hover:scale-100',
    // Outside months
    'data-[outside-view]:text-gray-500',
    // Unavailable outside months
    '[&[data-outside-view][data-unavailable]]:text-gray-200',
    // Hover animation
    'hover:scale-105',
    props.class
  )
)

const handleClick = (e: MouseEvent) => {
  const el = e.currentTarget as HTMLElement
  if (el?.ariaDisabled === 'true') {
    e.preventDefault()
    e.stopImmediatePropagation()
  }
}
</script>

<template>
  <CalendarCellTrigger as-child v-bind="forwardedProps">
    <button v-if="props.disableLink" :aria-label="ariaLabel" type="button" :class="cellClasses">
      {{ props.day.day }}
    </button>

    <!-- No prefetch: every month switch would render ~20 day pages on the server,
         and their API calls exhaust the visitor's rate limit -->
    <NuxtLinkLocale
      v-else
      :to="newsUrl"
      :prefetch="false"
      :aria-label="ariaLabel"
      :class="cellClasses"
      @click.capture="handleClick"
    >
      {{ props.day.day }}
    </NuxtLinkLocale>
  </CalendarCellTrigger>
</template>
